import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  model,
  OnInit,
  output,
  TemplateRef,
} from '@angular/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { TabsModule } from '@openng/optimus-ui/tabs';
import { CustomDialogContentComponent } from '../dialog/custom-dialog-content.component';
import { CustomDialogOption } from '../dialog/custom-dialog.component';

export type StepperTabConfig = {
  title: string;
  contentTemplate: TemplateRef<unknown>;
};

@Component({
  selector: 'core-stepper-tabs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TabsModule, ButtonModule],
  providers: [DialogService],
  template: `
    <p-tabs [(value)]="selectedIndex" (valueChange)="onTabChange($event!)">
      <p-tablist>
        @for (tab of tabs(); track $index) {
          <p-tab [value]="$index" [disabled]="saving()">{{ tab.title }}</p-tab>
        }
      </p-tablist>
      <p-tabpanels>
        @for (tab of tabs(); track $index) {
          <p-tabpanel [value]="$index">
            <ng-container
              [ngTemplateOutlet]="tab.contentTemplate"
            ></ng-container>
          </p-tabpanel>
        }
      </p-tabpanels>
    </p-tabs>
    <div class="stepper-toolbar">
      <div>
        @if (toolbarActionsTemplate(); as toolbarActionsTemplate) {
          <ng-container
            [ngTemplateOutlet]="toolbarActionsTemplate"
          ></ng-container>
        }
      </div>
      <p-button
        data-testid="btnCancel"
        i18n="@@btn-cancel"
        (click)="onCancel()"
        [disabled]="saving()"
        (mousedown)="$event.preventDefault()"
        severity="secondary"
        class="ml-auto"
        >Cancel</p-button
      >
    </div>
  `,
  styles: [
    `
      .stepper-toolbar {
        @apply flex gap-2 pt-3 pb-5 px-5;
        background: var(--p-tabs-tabpanel-background);
      }
    `,
  ],
})
export class StepperTabsComponent implements OnInit {
  public readonly tabs = input.required<StepperTabConfig[]>();
  public readonly initialIndex = input(0);
  public readonly selectedIndex = model(0);
  public readonly saving = input(false);
  public readonly toolbarActionsTemplate = input<TemplateRef<unknown> | null>(
    null,
  );
  public readonly confirmOnCancel = input(true);

  public readonly discard = output<void>();
  public readonly tabChanged = output<number>();

  private readonly dialogService = inject(DialogService);

  public ngOnInit() {
    this.selectedIndex.set(
      Math.min(
        Math.max(0, this.initialIndex()),
        Math.max(0, this.tabs().length - 1),
      ),
    );
  }

  public onTabChange(value: string | number) {
    this.tabChanged.emit(value as number);
  }

  public onCancel() {
    if (!this.confirmOnCancel()) {
      this.discard.emit();
      return;
    }
    const option: CustomDialogOption = {
      content: $localize`:@@alert-abandon-modify:You are about to lose your modifications, do you want to proceed?`,
      buttons: [
        {
          type: 'ok',
          text: $localize`:@@btn-discard:Discard`,
        },
        {
          type: 'cancel',
        },
      ],
    };

    const dialogRef = this.dialogService.open(CustomDialogContentComponent, {
      header: $localize`:@@alert-title-abandon-modify:Abandon Modifications`,
      modal: true,
      width: '50rem',
      data: option,
    });
    if (!dialogRef) {
      return;
    }
    dialogRef.onClose.subscribe((result: boolean) => {
      if (result) {
        this.discard.emit();
      }
    });
  }
}
